import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { SectionKicker, SectionTitle } from "../MarketingSection";
import LineIcon from "./LineIcon";

const ROW_STYLES = [
  {
    row: "bg-[linear-gradient(145deg,rgba(255,255,255,0.96),rgba(239,243,255,0.88))]",
    icon: "bg-[#E8EDFF] text-[#6170B3]",
  },
  {
    row: "bg-[linear-gradient(145deg,rgba(255,255,255,0.96),rgba(255,240,248,0.88))]",
    icon: "bg-[#F7E2F7] text-[#A15A9A]",
  },
  {
    row: "bg-[linear-gradient(145deg,rgba(255,255,255,0.96),rgba(241,252,226,0.9))]",
    icon: "bg-[#EBF7DB] text-[#687250]",
  },
];

type SavingsSectionProps = {
  kicker: string;
  title: ReactNode;
  items: { icon: ReactNode; text: string }[];
  result?: { title: string; body: string };
};

export default function SavingsSection({
  kicker,
  title,
  items,
  result,
}: SavingsSectionProps) {
  return (
    <section className="relative overflow-hidden bg-[linear-gradient(135deg,#fff_0%,#f8fbff_62%,#f4f7ff_100%)] px-[6vw] py-[105px] text-marketing-ink before:absolute before:-right-[150px] before:-top-[270px] before:size-[560px] before:rounded-full before:bg-[radial-gradient(circle,rgba(70,112,255,0.1),transparent_68%)] before:content-[''] max-[680px]:px-5 max-[680px]:py-[76px]">
      <div className="relative z-[1] mx-auto grid w-[min(1180px,100%)] grid-cols-[0.92fr_1.08fr] items-center gap-[clamp(55px,8vw,120px)] max-[980px]:grid-cols-1">
        <div className="relative self-center">
          <SectionKicker>{kicker}</SectionKicker>
          <SectionTitle className="max-[680px]:!text-[clamp(32px,9.7vw,52px)] [&_span]:whitespace-nowrap [&_span]:text-blue-600">
            {title}
          </SectionTitle>
          <span
            aria-hidden
            className="ml-[22px] mt-[35px] block h-[42px] w-[170px] -skew-x-[28deg] -rotate-[8deg] rounded-[50%] border-t-8 border-[rgba(58,215,207,0.45)]"
          />
        </div>
        <div>
          <ul className="m-0 grid list-none gap-[14px] p-0">
            {items.map((item, i) => (
              <li
                key={item.text}
                className={cn(
                  "grid min-h-[100px] grid-cols-[68px_1fr] items-center gap-[22px] rounded-[18px] border border-[#DCE3F4] px-[22px] py-[18px] text-marketing-navy shadow-[0_12px_34px_rgba(31,50,117,0.06)]",
                  ROW_STYLES[i % 3].row,
                )}
              >
                <span
                  className={cn(
                    "grid size-[58px] place-items-center rounded-[15px]",
                    ROW_STYLES[i % 3].icon,
                  )}
                >
                  <LineIcon className="size-[33px]">{item.icon}</LineIcon>
                </span>
                <strong className="font-figtree text-[19px] font-[850] leading-[1.35]">
                  {item.text}
                </strong>
              </li>
            ))}
          </ul>
          {result && (
            <div className="relative ml-[45px] mt-[30px] before:absolute before:-left-12 before:-top-[25px] before:h-[38px] before:w-[42px] before:-rotate-[9deg] before:rounded-[0_0_0_34px] before:border-b-[3px] before:border-l-[3px] before:border-blue-600 before:content-[''] after:absolute after:-left-4 after:top-1 after:size-[10px] after:-rotate-[42deg] after:border-b-[3px] after:border-r-[3px] after:border-blue-600 after:content-['']">
              <h3 className="m-0 font-figtree text-[clamp(28px,3.2vw,42px)] font-black uppercase leading-none tracking-[-0.045em] text-blue-600">
                {result.title}
              </h3>
              <p className="mt-[10px] font-open-sans text-[17px] leading-[1.55] text-marketing-muted">
                {result.body}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
